import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import ReactSelect, { components } from 'react-select';
import { ArrowDropDown } from 'views/ui/icons';
import { colors, fontSizes, shadows, shape, spacing } from '../utils';

const inputBackgroundColors = {
  default: {
    base: colors.white,
    highlight: colors.background.light,
  },
  invert: {
    base: colors.background.light,
    highlight: colors.white,
  },
};

// exported for easier testing
export const inputBackground = (invert, open, hover) => {
  const { base, highlight } = invert
    ? inputBackgroundColors.invert
    : inputBackgroundColors.default;
  return open || hover ? highlight : base;
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ArrowDropDown />
  </components.DropdownIndicator>
);

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  margin-right: ${(props) => props.marginRight};
  width: min-content;
`;

const Label = styled.div`
  align-items: center;
  color: ${colors.text.secondary};
  display: flex;
  font-size: ${fontSizes.small};
  margin-right: ${spacing.xxsmall};
  white-space: nowrap;
`;

// Custom styles for react-select components
const styles = {
  container: (base) => ({
    ...base,
    fontFamily: 'Soleil',
    fontSize: fontSizes.medium,
  }),
  control: (base, { menuIsOpen, selectProps }) => ({
    ...base,
    alignItems: 'center',
    background: inputBackground(selectProps.invert, menuIsOpen, false),
    border: 'none',
    borderRadius: shape.borderRadius,
    boxShadow: 'none',
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    height: '36px',
    minHeight: '36px',
    padding: spacing.xsmall,
    width: selectProps.inputWidth || 'max-content',
    '&:hover': {
      background: inputBackground(selectProps.invert, menuIsOpen, true),
      border: 'none',
      cursor: 'pointer',
    },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    flexShrink: 0,
    padding: 'none',
  }),
  group: (base) => ({
    ...base,
    padding: 0,
  }),
  groupHeading: (base) => ({
    ...base,
    boxSizing: 'border-box',
    color: colors.text.secondary,
    cursor: 'default',
    fontSize: fontSizes.small,
    fontWeight: 200,
    marginBottom: 0,
    padding: spacing.medium,
    paddingBottom: spacing.xsmall,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    marginLeft: '10px',
  }),
  menu: (base) => ({
    ...base,
    background: colors.white,
    borderRadius: shape.borderRadius,
    boxShadow: shadows.primary,
    boxSizing: 'border-box',
    marginBottom: 0,
    marginTop: spacing.xxsmall,
    position: 'absolute',
    width: 'max-content',
    zIndex: 1,
  }),
  menuList: (base) => ({
    ...base,
    boxSizing: 'border-box',
    maxHeight: '460px',
    minWidth: '140px',
    overflowY: 'auto',
    padding: `${spacing.xsmall} ${spacing.none}`,
    width: 'max-content',
    scrollbarColor: `${colors.border} ${colors.white}`,
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: colors.border,
      borderRadius: 'none',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    padding: `${spacing.xsmall} ${spacing.medium}`,
  }),
  option: (base, { isDisabled, isSelected, selectProps }) => ({
    ...base,
    background: 'transparent',
    boxSizing: 'border-box',
    color: isDisabled ? colors.text.icon : colors.text.primary,
    fontSize: fontSizes.medium,
    fontWeight: isSelected ? '700' : '400',
    padding: spacing.xsmall,
    paddingLeft: selectProps.grouped ? spacing.large : spacing.medium,
    paddingRight: spacing.xxlarge,
    whiteSpace: 'nowrap',
    width: '100%',
    '&:active': { background: colors.background.light },
    '&:hover': { background: colors.background.light },
  }),
  placeholder: (base) => ({
    ...base,
    color: colors.text.secondary,
    margin: 0,
    maxWidth: 'none',
    overflow: 'hidden',
    position: 'static',
    textOverflow: 'ellipsis',
    top: 0,
    transform: 'none',
    whiteSpace: 'nowrap',
    width: '100%',
  }),
  singleValue: (base) => ({
    ...base,
    color: colors.text.primary,
    margin: 0,
    maxWidth: 'none',
    overflow: 'hidden',
    position: 'static',
    textOverflow: 'ellipsis',
    top: 0,
    transform: 'none',
    whiteSpace: 'nowrap',
    width: '100%',
  }),
  valueContainer: (base) => ({
    ...base,
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    padding: 'none',
    position: 'static',
  }),
};

export const Select = ({
  defaultValue,
  disabled = false,
  disabledOptions = [],
  formId = '',
  grouped = false,
  inputWidth,
  invert = false,
  label,
  loading = false,
  maxMenuHeight,
  minMenuHeight,
  noOptionsMessage,
  onSelect,
  options = [],
  searchable = false,
  value,
}) => {
  const isOptionDisabled = (option) => {
    const disabledOptionsMatch = disabledOptions.some((opt) => {
      return opt.value === option.value && opt.label === option.label;
    });
    return !!option.isDisabled || disabledOptionsMatch;
  };

  return (
    <Container>
      <Label>{label}</Label>
      <ReactSelect
        // react-select props
        aria-label={`select-${label}`}
        components={{
          DropdownIndicator,
          IndicatorSeparator: null,
        }}
        defaultValue={defaultValue}
        form={formId}
        isDisabled={disabled}
        isLoading={loading}
        isOptionDisabled={isOptionDisabled}
        isSearchable={searchable}
        maxMenuHeight={maxMenuHeight}
        menuShouldScrollIntoView={false}
        minMenuHeight={minMenuHeight}
        name={label}
        noOptionsMessage={noOptionsMessage}
        onChange={onSelect}
        options={options}
        styles={styles}
        value={value}
        // custom props
        grouped={grouped}
        inputWidth={inputWidth}
        invert={invert}
      />
    </Container>
  );
};

Select.displayName = 'Select';

Select.propTypes = {
  defaultValue: PropTypes.object,
  disabled: PropTypes.bool,
  formId: PropTypes.string,
  grouped: PropTypes.bool,
  inputWidth: PropTypes.number,
  invert: PropTypes.bool,
  label: PropTypes.string,
  loading: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  minMenuHeight: PropTypes.number,
  noOptionsMessage: PropTypes.func,
  onSelect: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  searchable: PropTypes.bool,
  value: PropTypes.object,
};
